/**
 * ExteletCredentialsCard — EXTELET-V3 · Phase 7
 *
 * Lives on the agent's Registration tab. Renders:
 *   • Credentials card · all keys scoped to this agent (Gateway · SDK · MCP)
 *     with row-level actions: Rotate / Suspend / Reactivate / Revoke / Edit scope
 *   • Snippet card · Python · Java · MCP language tabs · Re-show button
 *
 * Plaintext keys are SHOWN ONCE post-mint (wizard) or post-rotation.
 * After that we only show key prefix + status — the operator pastes their
 * saved plaintext into the snippet placeholder. Real plaintext is never
 * stored server-side (bcrypt-hashed only).
 *
 * Polling: when the registration is PENDING_VERIFICATION the card polls
 * every 10s to catch the BE auto-verify on first call. When the status
 * flips to VERIFIED, the polling stops + a success toast fires.
 */
import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Key, Copy, Check, RefreshCw, PauseCircle, PlayCircle, Ban, Target,
  FileCode2, Eye, AlertTriangle, Clock, ShieldCheck,
} from 'lucide-react';
import { Card, Button, Pill, Modal } from './index';
import * as api from '../../services/api';
import { useToast } from '../../contexts/ToastContext';

const KEY_TYPE_META = {
  GATEWAY:     { label: 'Gateway key',     hint: 'Scopes inbound traffic', tone: 'info' },
  SDK:         { label: 'SDK key',          hint: 'Authenticates the @Governed decorator', tone: 'purple' },
  MCP_GATEWAY: { label: 'MCP Gateway key', hint: 'Authenticates MCP requests from off-the-shelf agents', tone: 'cyan' },
  PROVE7_CONTAINER: { label: 'Prove7 Container key', hint: 'Authenticates a standalone Prove7 agentlet container over the agent-bridge', tone: 'teal' },
};

const STATUS_TONE = {
  ACTIVE:    'success',
  ROTATING:  'warning',
  SUSPENDED: 'warning',
  REVOKED:   'danger',
  EXPIRED:   'muted',
};

export function ExteletCredentialsCard({ agent, registration, onRegistrationRefresh }) {
  const { addToast } = useToast();
  const [keys, setKeys] = useState(null);                   /* null = loading */
  /* A refusal is not an empty estate. Both loaders below used to collapse any
   * thrown error into "nothing here", which is the failure ARCHITECTURE_
   * PRINCIPLES Principle 8 calls out by name ("rendered blank rows rather than
   * an error — indistinguishable from having no connectors configured"). On
   * 2026-10-25 this card told a Protect user "0 key(s) scoped to this agent"
   * about an agent whose Gateway and SDK keys that same session had just
   * minted; the real event was a 403 on gateway.key.read. These two hold why
   * the answer is unknown, so the card can say so. */
  const [keysError, setKeysError] = useState(null);
  const [snippets, setSnippets] = useState(null);
  const [snippetsError, setSnippetsError] = useState(null);
  const [snippetLang, setSnippetLang] = useState('java');
  const [copied, setCopied] = useState({});                 /* per-key copy ack */

  /* Action modals */
  const [rotateForKey, setRotateForKey] = useState(null);
  const [suspendForKey, setSuspendForKey] = useState(null);
  const [revokeForKey, setRevokeForKey] = useState(null);
  const [scopeForKey, setScopeForKey] = useState(null);
  const [rotateBusy, setRotateBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);

  /* Plaintext that surfaces once after rotation — same forcing-function
   * shape as the wizard step 4 reveal. */
  const [postRotateReveal, setPostRotateReveal] = useState(null);

  /* First-call observer polling. Active only when registration is in
   * PENDING_VERIFICATION; tears down once VERIFIED arrives. */
  const pollRef = useRef(null);
  const lastStatusRef = useRef(null);

  const loadKeys = useCallback(async () => {
    if (!agent?.id) return;
    try {
      const res = await api.gatewayListKeysByAgent(agent.id);
      const list = res?.data || res || [];
      setKeys(Array.isArray(list) ? list : []);
      setKeysError(null);
    } catch (err) {
      /* Branch on err.status, never on the message — apiFetch attaches it
         (services/api.js:334) and Principle 8 requires it. */
      setKeys(null);
      setKeysError(err?.status === 403 ? 'denied' : 'error');
    }
  }, [agent?.id]);

  const loadSnippets = useCallback(async () => {
    if (!registration?.id) return;
    try {
      const res = await api.gelRegenerateSnippets(registration.id);
      const env = res?.data || res || null;
      setSnippets(env);
      setSnippetsError(null);
    } catch (err) {
      setSnippets(null);
      setSnippetsError(err?.status === 403 ? 'denied' : 'error');
    }
  }, [registration?.id]);

  useEffect(() => { loadKeys(); }, [loadKeys]);
  useEffect(() => { loadSnippets(); }, [loadSnippets]);

  /* First-call observer — only mounts when registration is PENDING.
   * Polls /registrations/agent/{id} every 10s; clears interval once
   * we see VERIFIED. Fires a one-time success toast on the flip. */
  useEffect(() => {
    if (!registration || !agent?.id) return;
    if (registration.registrationStatus !== 'PENDING_VERIFICATION') return;
    if (pollRef.current) return;

    lastStatusRef.current = 'PENDING_VERIFICATION';
    const tick = async () => {
      try {
        const res = await api.gelGetRegistrationByAgent?.(agent.id);
        const row = res?.data || res;
        const next = row?.registrationStatus;
        if (next && next !== lastStatusRef.current) {
          lastStatusRef.current = next;
          if (next === 'VERIFIED') {
            addToast({ tone: 'success',
              title: 'First call observed · auto-verified',
              description: 'The first successful protected call landed. Registration is now VERIFIED.' });
            onRegistrationRefresh?.();
          }
        }
      } catch (e) { /* ignore — transient */ }
    };
    pollRef.current = setInterval(tick, 10_000);
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, [registration?.registrationStatus, agent?.id, addToast, onRegistrationRefresh]);

  const copyTo = (k, v) => {
    try {
      navigator.clipboard?.writeText(v);
      setCopied(c => ({ ...c, [k]: true }));
      setTimeout(() => setCopied(c => ({ ...c, [k]: false })), 1500);
    } catch (e) { /* clipboard unavailable in some embedded contexts */ }
  };

  const doRotate = async (graceHours) => {
    if (!rotateForKey) return;
    setRotateBusy(true);
    try {
      const res = await api.gatewayRotateKey(rotateForKey.id, graceHours ?? 24);
      const env = res?.data || res || {};
      /* DEF-EXTELET-V3-ROTATE-001 — BE shape is { newKey: {...}, plaintextKey, warning }.
       * The old fallback chain looked for env.key which never existed, so
       * keyId/prefix/keyType were silently undefined on the post-rotation
       * reveal. Read newKey explicitly. */
      const newKey = env.newKey || env.key || {};
      const plaintext = env.plaintextKey || env.plaintext || newKey.plaintextKey;
      if (!plaintext) {
        const errMsg = env.error || res?.error || 'Server did not return new plaintext.';
        addToast({ tone: 'danger', title: 'Rotation failed', description: errMsg });
        return;
      }
      setPostRotateReveal({
        keyId: newKey.id,
        prefix: newKey.keyPrefix,
        keyType: newKey.keyType || rotateForKey.keyType,
        plaintext,
        graceHours: graceHours ?? 24,
      });
      setRotateForKey(null);
      loadKeys();
      addToast({ tone: 'success', title: 'Key rotated',
        description: `New plaintext below. Old key remains valid for ${graceHours ?? 24}h grace.` });
    } catch (err) {
      addToast({ tone: 'danger', title: 'Rotation failed', description: err?.message || String(err) });
    } finally {
      setRotateBusy(false);
    }
  };

  const doSuspend = async (reason) => {
    if (!suspendForKey) return;
    setActionBusy(true);
    try {
      const res = await api.gatewaySuspendKey(suspendForKey.id, reason || 'Manually suspended from Registration tab');
      if (res && res.ok === false) {
        addToast({ tone: 'danger', title: 'Suspend failed', description: res.error || `HTTP ${res.status}` });
        return;
      }
      addToast({ tone: 'info', title: 'Key suspended', description: 'Requests with this key will be rejected.' });
      setSuspendForKey(null);
      loadKeys();
    } catch (err) {
      addToast({ tone: 'danger', title: 'Suspend failed', description: err?.message || String(err) });
    } finally { setActionBusy(false); }
  };

  const doReactivate = async (key) => {
    setActionBusy(true);
    try {
      const res = await api.gatewayReactivateKey(key.id);
      if (res && res.ok === false) {
        addToast({ tone: 'danger', title: 'Reactivate failed', description: res.error || `HTTP ${res.status}` });
        return;
      }
      addToast({ tone: 'success', title: 'Key reactivated' });
      loadKeys();
    } catch (err) {
      addToast({ tone: 'danger', title: 'Reactivate failed', description: err?.message || String(err) });
    } finally { setActionBusy(false); }
  };

  const doRevoke = async (reason) => {
    if (!revokeForKey) return;
    setActionBusy(true);
    try {
      const res = await api.gatewayRevokeKey(revokeForKey.id, reason || 'Manually revoked from Registration tab');
      if (res && res.ok === false) {
        addToast({ tone: 'danger', title: 'Revoke failed', description: res.error || `HTTP ${res.status}` });
        return;
      }
      addToast({ tone: 'success', title: 'Key revoked permanently' });
      setRevokeForKey(null);
      loadKeys();
    } catch (err) {
      addToast({ tone: 'danger', title: 'Revoke failed', description: err?.message || String(err) });
    } finally { setActionBusy(false); }
  };

  const doUpdateScope = async (scopedAgentIds) => {
    if (!scopeForKey) return;
    setActionBusy(true);
    try {
      const res = await api.gatewayUpdateKeyScope(scopeForKey.id, scopedAgentIds);
      if (res && res.ok === false) {
        addToast({ tone: 'danger', title: 'Edit scope failed', description: res.error || `HTTP ${res.status}` });
        return;
      }
      addToast({ tone: 'success', title: 'Scope updated' });
      setScopeForKey(null);
      loadKeys();
    } catch (err) {
      addToast({ tone: 'danger', title: 'Edit scope failed', description: err?.message || String(err) });
    } finally { setActionBusy(false); }
  };

  if (!registration || !registration.id) return null;

  return (
    <>
      {/* ── Credentials card ─────────────────────────────────────── */}
      <Card padding="sm" className="mt-3" data-card-id="credentials">
        <Card.Header
          title={<span className="flex items-center gap-2">
            <Key size={14} aria-hidden="true" /> Credentials{keysError
              ? ''
              : ` · ${keys?.length ?? '…'} key(s) scoped to this agent`}
          </span>}
        />
        <Card.Body>
          {keys == null && !keysError && (
            <p className="text-[11.5px] text-v2-ink-muted">Loading keys…</p>
          )}
          {/* Refused, so the count is unknown — not zero. Saying "no keys" here
              is a false statement about a security-relevant surface: this agent
              may well hold keys that this session simply may not read. */}
          {keysError === 'denied' && (
            <p className="text-[12px] text-v2-ink-muted flex items-start gap-2">
              <ShieldCheck size={14} aria-hidden="true" className="mt-0.5 flex-shrink-0" />
              <span>
                You do not have permission to view this agent&rsquo;s keys, so we cannot show
                whether it has any. An administrator can see them on the Protected Gateway page.
              </span>
            </p>
          )}
          {keysError === 'error' && (
            <p className="text-[12px] text-v2-ink-muted flex items-start gap-2" role="alert">
              <AlertTriangle size={14} aria-hidden="true" className="mt-0.5 flex-shrink-0" />
              <span>We could not load this agent&rsquo;s keys just now. Reload to try again.</span>
            </p>
          )}
          {keys != null && keys.length === 0 && (
            <p className="text-[12px] text-v2-ink-muted">
              No keys scoped to this agent. Promote via the wizard to mint a Gateway + SDK or MCP key, or use the
              Protected Gateway page to manually issue one.
            </p>
          )}
          {keys != null && keys.length > 0 && (
            <ul className="divide-y divide-v2-border">
              {keys.map((k) => {
                const meta = KEY_TYPE_META[k.keyType] || { label: k.keyType, hint: '', tone: 'muted' };
                return (
                  <li key={k.id} className="py-2 flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Pill tone={meta.tone} size="sm">{meta.label}</Pill>
                        <Pill tone={STATUS_TONE[k.status] || 'muted'} size="sm" dot>{k.status}</Pill>
                        <code className="font-mono-v2 text-[11.5px] text-v2-ink truncate">
                          {k.keyPrefix}…<span className="text-v2-ink-muted">{k.id?.substring(0, 6)}</span>
                        </code>
                        <button
                          type="button"
                          onClick={() => copyTo('prefix-' + k.id, k.keyPrefix)}
                          className="text-[10.5px] text-v2-info hover:underline inline-flex items-center gap-1"
                          aria-label="Copy key prefix"
                        >
                          {copied['prefix-' + k.id] ? <Check size={11} /> : <Copy size={11} />}
                          {copied['prefix-' + k.id] ? 'Copied prefix' : 'Copy prefix'}
                        </button>
                      </div>
                      <p className="text-[10.5px] text-v2-ink-muted mt-0.5">
                        {meta.hint}
                        {' · '}rate {k.rateLimitRpm}/min · burst {k.rateLimitBurst}
                        {k.lastUsedAt && <> · last used {new Date(k.lastUsedAt).toLocaleString()}</>}
                        {k.totalInvocations > 0 && <> · {k.totalInvocations} invocations</>}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {k.status === 'ACTIVE' && (
                        <>
                          <Button variant="ghost" size="sm" iconLeft={<RefreshCw size={11} />}
                                  onClick={() => setRotateForKey(k)} title="Rotate with grace period">Rotate</Button>
                          <Button variant="ghost" size="sm" iconLeft={<Target size={11} />}
                                  onClick={() => setScopeForKey(k)} title="Edit scoped agent IDs">Scope</Button>
                          <Button variant="ghost" size="sm" iconLeft={<PauseCircle size={11} />}
                                  onClick={() => setSuspendForKey(k)} title="Suspend (temporary disable)">Suspend</Button>
                        </>
                      )}
                      {k.status === 'SUSPENDED' && (
                        <Button variant="ghost" size="sm" iconLeft={<PlayCircle size={11} />}
                                onClick={() => doReactivate(k)} loading={actionBusy}>Reactivate</Button>
                      )}
                      {k.status !== 'REVOKED' && (
                        <Button variant="ghost" size="sm" iconLeft={<Ban size={11} />}
                                onClick={() => setRevokeForKey(k)}
                                title="Permanently revoke — cannot undo">Revoke</Button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          {/* Auto-verify observer banner — only visible while PENDING */}
          {registration.registrationStatus === 'PENDING_VERIFICATION' && (
            <div className="mt-3 rounded-v2-md bg-v2-info-soft text-v2-info-strong px-3 py-2 text-[11.5px] flex items-start gap-2">
              <Clock size={14} aria-hidden="true" className="flex-shrink-0 mt-[2px]" />
              <span>
                <strong className="font-semibold">Waiting for first protected call…</strong>{' '}
                Polling every 10s. When the agent makes its first authenticated call carrying any of the keys above,
                the registration auto-flips to <strong className="font-semibold">VERIFIED</strong>.
              </span>
            </div>
          )}
          {registration.registrationStatus === 'VERIFIED' && registration.verifiedBy
            && String(registration.verifiedBy).toLowerCase() === '00000000-0000-0000-0000-aaaaffffffff' && (
            <div className="mt-3 rounded-v2-md bg-v2-success-soft text-v2-success-strong px-3 py-2 text-[11.5px] flex items-start gap-2">
              <ShieldCheck size={14} aria-hidden="true" className="flex-shrink-0 mt-[2px]" />
              <span>
                <strong className="font-semibold">Auto-verified.</strong>{' '}
                First governed call landed at {registration.verifiedAt ? new Date(registration.verifiedAt).toLocaleString() : '—'}.
                Audit row: <code className="font-mono-v2 text-[11px]">EXTERNAL_AGENT_VERIFIED_FIRST_CALL</code>.
              </span>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* ── Snippet card — re-show language-tabbed integration code ─────── */}
      <Card padding="sm" className="mt-3" data-card-id="snippet">
        <Card.Header
          title={<span className="flex items-center gap-2">
            <FileCode2 size={14} aria-hidden="true" /> Integration snippet
          </span>}
          actions={
            <Button variant="ghost" size="sm" iconLeft={<RefreshCw size={11} />} onClick={loadSnippets}>
              Re-show
            </Button>
          }
        />
        <Card.Body>
          <div className="inline-flex gap-0 bg-v2-muted p-0.5 rounded-v2-sm mb-2">
            {['java', 'python', 'mcp'].map((lang) => (
              <button
                key={lang} type="button" onClick={() => setSnippetLang(lang)}
                className={`px-3 py-1 text-[11.5px] rounded-v2-sm transition-colors duration-v2 ${
                  snippetLang === lang ? 'bg-v2-surface text-v2-ink font-semibold' : 'text-v2-ink-secondary hover:text-v2-ink'
                }`}>
                {lang === 'java' ? 'Java · Spring AOP' : lang === 'python' ? 'Python' : 'MCP config'}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-v2-ink-muted mb-2">
            Plaintext key isn't stored server-side. Paste your saved <code className="font-mono-v2">agt_sk_…</code> or{' '}
            <code className="font-mono-v2">agt_mcp_…</code> in place of the placeholder.
            Need a fresh plaintext? Use <strong>Rotate</strong> on the key above.
          </p>
          <SnippetBlock
            code={
              snippets?.java && snippetLang === 'java'   ? snippets.java
            : snippets?.python && snippetLang === 'python' ? snippets.python
            : snippets?.mcpConfig && snippetLang === 'mcp' ? snippets.mcpConfig
            /* Never sit on "Loading…" forever: before this, a refusal or a
               failed regenerate left that placeholder on screen permanently,
               which reads as a slow page rather than a thing that went wrong. */
            : snippetsError === 'denied'
              ? '// You do not have permission to regenerate this snippet.\n'
                + '// An administrator can re-show it from the Protected Gateway page.'
            : snippetsError
              ? '// Could not load the snippet just now. Reload to try again.'
            : '// Loading snippet…'
            }
            onCopy={() => copyTo('snippet',
              snippetLang === 'java' ? snippets?.java
            : snippetLang === 'python' ? snippets?.python
            : snippets?.mcpConfig)}
            copied={copied.snippet}
          />
        </Card.Body>
      </Card>

      {/* ── Rotate modal ─────────────────────────────────────────────── */}
      <RotateKeyModal
        keyRow={rotateForKey}
        onClose={() => setRotateForKey(null)}
        onConfirm={doRotate}
        busy={rotateBusy}
      />

      {/* ── Post-rotation plaintext reveal modal ─────────────────────── */}
      <PostRotateReveal
        reveal={postRotateReveal}
        onClose={() => setPostRotateReveal(null)}
        onCopy={(v) => copyTo('rotated-plaintext', v)}
        copied={copied['rotated-plaintext']}
      />

      {/* ── Suspend modal ────────────────────────────────────────────── */}
      <ReasonModal
        keyRow={suspendForKey}
        title="Suspend key"
        description="Temporarily reject requests carrying this key. Reactivate at any time."
        confirmLabel="Suspend"
        confirmVariant="warning"
        onClose={() => setSuspendForKey(null)}
        onConfirm={doSuspend}
        busy={actionBusy}
        reasonRequired={true}
      />

      {/* ── Revoke modal ─────────────────────────────────────────────── */}
      <ReasonModal
        keyRow={revokeForKey}
        title="Revoke key permanently"
        description="Cannot be undone. The key will fail all future authentication. Issue a new one if needed."
        confirmLabel="Revoke"
        confirmVariant="danger"
        onClose={() => setRevokeForKey(null)}
        onConfirm={doRevoke}
        busy={actionBusy}
        reasonRequired={true}
      />

      {/* ── Edit scope modal ─────────────────────────────────────────── */}
      <EditScopeModal
        keyRow={scopeForKey}
        currentAgent={agent}
        onClose={() => setScopeForKey(null)}
        onConfirm={doUpdateScope}
        busy={actionBusy}
      />
    </>
  );
}

/* ─────────────────────────────────────────────────────────────────────── */

function SnippetBlock({ code, onCopy, copied }) {
  return (
    <div className="relative">
      <button
        type="button" onClick={onCopy}
        className="absolute top-2 right-2 bg-v2-ink/90 text-v2-page border border-v2-border-strong px-2 py-0.5 text-[10.5px] rounded-v2-sm hover:bg-v2-ink z-10 inline-flex items-center gap-1"
      >
        {copied ? <Check size={11} /> : <Copy size={11} />}
        {copied ? 'Copied' : 'Copy'}
      </button>
      <pre className="bg-[#0e1116] text-[#d6deec] font-mono-v2 text-[11.5px] p-3 rounded-v2-md overflow-x-auto whitespace-pre leading-relaxed">
        {code}
      </pre>
    </div>
  );
}

function RotateKeyModal({ keyRow, onClose, onConfirm, busy }) {
  const [graceHours, setGraceHours] = useState(24);
  useEffect(() => { if (keyRow) setGraceHours(24); }, [keyRow?.id]);
  return (
    <Modal
      isOpen={!!keyRow}
      onClose={onClose}
      title={keyRow ? `Rotate ${KEY_TYPE_META[keyRow.keyType]?.label || keyRow.keyType}` : 'Rotate key'}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="primary" onClick={() => onConfirm(graceHours)} loading={busy}
                  iconLeft={<RefreshCw size={13} />}>Rotate</Button>
        </>
      }
    >
      {keyRow && (
        <div className="text-[12.5px] leading-relaxed">
          <p>
            A fresh plaintext key is minted and returned <strong>once</strong>. The old key (<code className="font-mono-v2">{keyRow.keyPrefix}…</code>)
            stays valid during the grace period so deployed agents keep working while you roll the new key out.
          </p>
          <label className="block mt-3">
            <span className="text-[10.5px] uppercase tracking-wider text-v2-ink-muted block mb-1">Grace period (hours)</span>
            <input
              type="number" min={1} max={168} value={graceHours}
              onChange={(e) => setGraceHours(Math.max(1, Math.min(168, Number(e.target.value) || 24)))}
              className="v2-focus-ring w-32 bg-v2-surface border border-v2-border rounded-v2-sm px-2 py-1 text-[13px]"
            />
            <span className="text-[10.5px] text-v2-ink-muted ml-2">old key valid for this long</span>
          </label>
        </div>
      )}
    </Modal>
  );
}

function PostRotateReveal({ reveal, onClose, onCopy, copied }) {
  return (
    <Modal
      isOpen={!!reveal}
      onClose={null} /* forcing-function — operator must explicitly Close after saving */
      dismissOnBackdrop={false}
      title={reveal ? `New ${KEY_TYPE_META[reveal.keyType]?.label || reveal.keyType} — save this now` : 'New key'}
      size="md"
      footer={<Button variant="primary" onClick={onClose}>I've saved this key</Button>}
    >
      {reveal && (
        <div className="text-[12.5px] leading-relaxed">
          <div className="rounded-v2-md bg-v2-warning-soft text-v2-warning-strong px-3 py-2 mb-3 flex items-start gap-2">
            <AlertTriangle size={14} aria-hidden="true" className="flex-shrink-0 mt-[2px]" />
            <span>
              <strong>This plaintext will never be shown again.</strong> Store it in your secrets manager
              before clicking the button below. The old key remains valid for {reveal.graceHours}h.
            </span>
          </div>
          <div className="bg-v2-muted px-3 py-2 rounded-v2-md flex items-center justify-between gap-2">
            <code className="font-mono-v2 text-[12px] break-all">{reveal.plaintext}</code>
            <button type="button" onClick={() => onCopy(reveal.plaintext)}
                    className="text-v2-info text-[11.5px] inline-flex items-center gap-1 flex-shrink-0 hover:underline">
              {copied ? <Check size={12} /> : <Copy size={12} />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}

function ReasonModal({ keyRow, title, description, confirmLabel, confirmVariant, onClose, onConfirm, busy, reasonRequired }) {
  const [reason, setReason] = useState('');
  useEffect(() => { if (keyRow) setReason(''); }, [keyRow?.id]);
  return (
    <Modal
      isOpen={!!keyRow}
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button
            variant={confirmVariant === 'danger' ? 'danger' : 'primary'}
            onClick={() => onConfirm(reason)}
            loading={busy}
            disabled={reasonRequired && !reason.trim()}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      {keyRow && (
        <div className="text-[12.5px] leading-relaxed">
          <p>{description}</p>
          <p className="mt-2 text-[11.5px] text-v2-ink-muted">
            Key: <code className="font-mono-v2">{keyRow.keyPrefix}…</code> ({KEY_TYPE_META[keyRow.keyType]?.label})
          </p>
          <label className="block mt-3">
            <span className="text-[10.5px] uppercase tracking-wider text-v2-ink-muted block mb-1">
              Reason {reasonRequired && <span className="text-v2-danger-strong">*</span>}
            </span>
            <textarea
              rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="Audit log captures this verbatim"
              className="v2-focus-ring w-full bg-v2-surface border border-v2-border rounded-v2-sm px-2 py-1 text-[12.5px]"
            />
          </label>
        </div>
      )}
    </Modal>
  );
}

function EditScopeModal({ keyRow, currentAgent, onClose, onConfirm, busy }) {
  const [scopedIds, setScopedIds] = useState('');
  useEffect(() => {
    if (!keyRow) return;
    const raw = keyRow.scopedAgentIds;
    const list = Array.isArray(raw) ? raw : (raw?.length ? raw : []);
    setScopedIds(Array.from(list).join('\n'));
  }, [keyRow?.id]);
  const onlyThisAgent = () => setScopedIds(currentAgent?.id || '');
  const tenantWide = () => setScopedIds('');
  return (
    <Modal
      isOpen={!!keyRow}
      onClose={onClose}
      title="Edit key scope"
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="primary" onClick={() => {
            const list = scopedIds.split('\n').map(s => s.trim()).filter(Boolean);
            onConfirm(list);
          }} loading={busy}>Save scope</Button>
        </>
      }
    >
      {keyRow && (
        <div className="text-[12.5px] leading-relaxed">
          <p className="mb-2">
            Limit which agents this key is allowed to invoke. One UUID per line; empty list = tenant-wide (any agent).
          </p>
          <div className="flex gap-2 mb-2">
            <Button variant="ghost" size="sm" onClick={onlyThisAgent}>Lock to this agent only</Button>
            <Button variant="ghost" size="sm" onClick={tenantWide}>Make tenant-wide</Button>
          </div>
          <textarea
            rows={5}
            value={scopedIds}
            onChange={(e) => setScopedIds(e.target.value)}
            placeholder="one Agent UUID per line — leave empty for tenant-wide"
            className="v2-focus-ring w-full bg-v2-surface border border-v2-border rounded-v2-sm px-2 py-1 text-[11.5px] font-mono-v2"
          />
        </div>
      )}
    </Modal>
  );
}

export default ExteletCredentialsCard;
